// RITA-1504 (Nutzungserfassung Phase 2b) — Formular-Vorgänge (Entwurf C).
//
// Ein Automat je Maske. Er kennt weder React noch Formik: die Komponente meldet Öffnen,
// Zustandsänderungen, den AUSGANG des Absendens und das Schließen.
//
// Warum der Ausgang ausdrücklich gemeldet wird: Formik setzt isSubmitting bei einem synchronen
// onSubmit sofort zurück. Ein beobachteter Schalter würde den Ausgang deshalb verfehlen oder
// doppelt zählen — submit_ok/submit_fail entstehen ausschließlich in submitted().
//
// Abbruch (abandon) meldet nur, wer wirklich etwas verwirft: geändert, kein gemeldeter Ausgang
// seit dem Öffnen und kein laufendes Absenden. Nach einem gemeldeten Ausgang ist die Maske
// entschieden — nach 'ok' sogar frisch (gespeichert), nach 'fail' hat der Server geantwortet.
// Der Automat sieht nur Zähler und Schalter, nie Eingabewerte.
//
// Sitzung (KI-Review !727, 2. Runde): Ein Automat gehört zu EINER Maskensitzung. Wird die Maske
// geschlossen, während ein Absenden läuft (Antwort steht aus), bleibt die Sitzung erhalten und
// meldet den späten Ausgang mit ihren eigenen Werten; ein Ausgang, der nach einer beendeten
// Sitzung eintrifft, wird verworfen. Masken auf parametrisierten Routen erzeugen ihren Automaten
// deshalb je Datensatz (useFormUsageMachine mit `session`) — sonst erbte die nächste Sitzung den
// Ausgang der vorigen und ihr Abbruch bliebe unterdrückt.
import type { UsageFormName } from '../usageKeys';
import type { UsageEventInput, UsageRecord } from './tracker';
import { buildFormProps } from './usageProps';
import type { FormOp, FormPropsInput } from './usageProps';

/** Ausgang des Absendens, wie ihn die Komponente kennt (Antwort da / Fehler). */
export type FormOutcome = 'ok' | 'fail';

/** Was der Automat vom Formular wissen muss — mehr nicht. */
export interface FormUsageState {
  /** Anzahl der Felder, die vom Ausgangswert abweichen. */
  changedFields: number;
  dirty: boolean;
  submitting: boolean;
}

export interface FormUsageDeps {
  now?: () => number;
  /** Kennung der Maskensitzung (z. B. Datensatznummer); nur zur Identität, wird nie gemeldet. */
  session?: string;
}

export interface FormUsageMachine {
  /** Kennung der Sitzung, an die der Automat gebunden ist (siehe useFormUsageMachine). */
  readonly session?: string;
  opened(): void;
  update(state: FormUsageState): void;
  /** Ausgang des Absendens; `invalidFields` nur Feld-NAMEN und nur beim Fehlschlag. */
  submitted(outcome: FormOutcome, invalidFields?: readonly string[]): void;
  closed(): void;
}

export const createFormUsageMachine = (
  record: UsageRecord,
  name: UsageFormName | string,
  deps: FormUsageDeps = {},
): FormUsageMachine => {
  const now = deps.now ?? (() => Date.now());

  let openedAt: number | null = null;
  let changedFields = 0;
  let dirty = false;
  let submitting = false;
  // Seit dem letzten Öffnen wurde ein Ausgang gemeldet: dann ist nichts verworfen worden.
  let settled = false;
  /** Lebenslauf der Sitzung: nie geöffnet, offen, geschlossen. */
  let phase: 'idle' | 'open' | 'closed' = 'idle';
  // Beim Schließen lief noch ein Absenden: die Sitzung wartet auf ihren späten Ausgang.
  let outcomePending = false;

  /** Ein Ereignis darf nie in die Bedienung durchschlagen (Entwurf A.3). */
  const emit = (input: FormPropsInput): void => {
    const event: UsageEventInput = { type: 'form', name, ts: now(), props: buildFormProps(input) };
    try {
      record(event);
    } catch {
      // Erfassung darf die Bedienung nicht stören.
    }
  };

  /** Öffnungsdauer; ohne bekannte Startzeit entfällt sie (Entwurf C). */
  const msOpen = (): number | undefined => (openedAt === null ? undefined : now() - openedAt);

  const emitOutcome = (op: FormOp, invalidFields?: readonly string[]): void => {
    const input: FormPropsInput = { op, changed_fields: changedFields };
    const elapsed = msOpen();
    if (elapsed !== undefined) input.ms_open = elapsed;
    if (invalidFields && invalidFields.length > 0) input.invalid_fields = invalidFields;
    emit(input);
  };

  const reset = (): void => {
    openedAt = null;
    changedFields = 0;
    dirty = false;
    submitting = false;
    settled = false;
  };

  return {
    session: deps.session,
    opened(): void {
      reset();
      phase = 'open';
      openedAt = now();
      // Kein ms_open am open-Ereignis: die Maske ist gerade erst aufgegangen.
      emit({ op: 'open' });
    },
    update(state: FormUsageState): void {
      changedFields = state.changedFields;
      dirty = state.dirty;
      submitting = state.submitting;
    },
    submitted(outcome: FormOutcome, invalidFields?: readonly string[]): void {
      // Der Ausgang einer beendeten Sitzung gehört zu niemandem mehr.
      if (phase === 'closed' && !outcomePending) return;
      settled = true;
      submitting = false;
      if (outcome === 'ok') {
        dirty = false;
        emitOutcome('submit_ok');
      } else {
        emitOutcome('submit_fail', invalidFields);
      }
      // Der späte Ausgang nach dem Schließen beendet die Sitzung endgültig.
      if (outcomePending) {
        outcomePending = false;
        reset();
      }
    },
    closed(): void {
      if (phase === 'closed') return;
      if (phase === 'open' && submitting) {
        // Antwort steht aus: kein Abbruch, die Werte bleiben für den späten Ausgang erhalten.
        phase = 'closed';
        outcomePending = true;
        return;
      }
      if (dirty && !settled && !submitting) emitOutcome('abandon');
      phase = 'closed';
      reset();
    },
  };
};
